import React, { useMemo, useRef, useState } from "react";
import { usePage } from "@inertiajs/inertia-react";
import { LabeledInput } from "@/Components/Form";
import { useAtom, useAtomValue } from "jotai";
import { focusAtom } from "jotai-optics";
import { splitAtom } from "jotai/utils";
import { AlertCircle, MoreVertical, Plus, Trash } from "react-feather";
import { uuid } from "@/Utilities/Utilities";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DefinitionOfScopes } from "@/Components/DefinitionOfScopes";
import { FormError } from "@/Components";
import { IconBookMedical } from "@/Icons";
import { useSetAtom } from "jotai/index";
import { isEmissionFactorExplorerOpenAtom } from "@/Components/Questions/QuestionBaseForm";

function SortableItem({
  item,
  i,
  emissionEnabled,
  collectFromUserEnabled,
  isMultiSelect,
  errorBag,
  onRemove = () => null,
}) {
  const readItem = useAtomValue(item);
  return (
    <InputTypeItem
      id={readItem.uuid}
      sortIndex={i}
      emissionEnabled={emissionEnabled}
      collectFromUserEnabled={collectFromUserEnabled}
      isMultiSelect={isMultiSelect}
      errorBag={errorBag}
      item={item}
      onRemove={onRemove}
    />
  );
}

function InputTypeItem({
  item,
  sortIndex,
  emissionEnabled,
  collectFromUserEnabled,
  isMultiSelect,
  errorBag,
  id,
  onRemove = () => null,
}) {
  const [payload, setPayload] = useAtom(item);

  const setIsEmissionFactorExplorerOpen = useSetAtom(
    isEmissionFactorExplorerOpenAtom,
  );

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const errors = usePage().props?.errors || {};
  const factors = usePage().props?.factors?.data || [];
  const question = usePage().props?.question?.data || {};

  const handleFormUpdate = (e) => {
    const { name, value } = e.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  const factor =
    (payload?.emission_factor_id &&
      factors.find((f) => f.id === payload?.emission_factor_id)) ||
    question?.inputs?.find((i) => i.id === payload?.id)?.emission_factor ||
    null;

  return (
    <div className="flex w-full items-center" ref={setNodeRef} style={style}>
      <button className="mt-2 flex cursor-grab" {...attributes} {...listeners}>
        <MoreVertical className="-ml-2" stroke="#1F4444" />
        <MoreVertical className="-ml-4" stroke="#1F4444" />
      </button>
      <div className="flex grow border-b-borderColor max-md:mb-4 max-md:flex-wrap  max-md:border-b-[1px] md:gap-4">
        <LabeledInput
          name="label"
          title={`Alternative ${sortIndex + 1}${
            sortIndex === 0 && !isMultiSelect ? " (Default alternative)" : ""
          }*`}
          placeholder={`Alternative ${sortIndex + 1}*`}
          className="mb-4"
          value={payload?.label || ""}
          onChange={handleFormUpdate}
          containerClass="grow shrink-0 select-none"
          error={
            errors?.[errorBag] &&
            errors?.[errorBag]?.[`inputs.${sortIndex}.label`]
          }
        />

        {emissionEnabled && (
          <>
            <div
              className={`mb-4 w-full  ${collectFromUserEnabled ? "lg:w-1/2" : "lg:w-1/3"}`}
            >
              <span className="mb-1 block text-[14px]">Emission factor*</span>

              {factor ? (
                <div className="mb-4 block flex min-h-[46px] w-full items-center justify-between border border-borderColor bg-white px-3 py-2 text-gray-700">
                  <p>
                    <span>{factor.name}</span>

                    <span className="ml-2 text-typo-inactive">
                      {factor.unit || "-"}
                    </span>
                  </p>
                  <button
                    onClick={() => setIsEmissionFactorExplorerOpen(sortIndex)}
                    className="text-blue underline hover:no-underline"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsEmissionFactorExplorerOpen(sortIndex)}
                  className="flex min-h-[46px] w-full items-center gap-2 border border-dashed border-gray-darker px-3 py-2 text-blue underline hover:no-underline"
                >
                  <IconBookMedical />
                  Add emission factor
                </button>
              )}

              <FormError
                error={
                  errors?.[errorBag]?.[`inputs.${sortIndex}.emission_factor_id`]
                }
              />
            </div>

            {/*<LabeledAutocomplete*/}
            {/*  name="emission_factor_id"*/}
            {/*  title="Choose emission factor*"*/}
            {/*  options={factorOptions}*/}
            {/*  placeholder="Select the right emission factor"*/}
            {/*  className="mb-4 select-none lg:w-1/3"*/}
            {/*  onSelect={handleFormUpdate}*/}
            {/*  defaultValue={payload?.emission_factor_id || ""}*/}
            {/*  error={*/}
            {/*    errors?.[errorBag] &&*/}
            {/*    errors?.[errorBag]?.[`inputs.${sortIndex}.emission_factor_id`]*/}
            {/*  }*/}
            {/*/>*/}
            {!collectFromUserEnabled && (
              <LabeledInput
                name="emission_amount"
                title="Amount"
                placeholder="Add amount"
                className="mb-4"
                containerClass="w-full md:w-40 shrink-0 select-none"
                value={payload?.emission_amount || ""}
                onChange={handleFormUpdate}
                unit={
                  factors.find((f) => f.id === payload?.emission_factor_id)
                    ?.unit
                }
              />
            )}
          </>
        )}
      </div>
      <span className="hover-circle relative ml-4 mt-2">
        <Trash className="cursor-pointer" onClick={onRemove} />
      </span>
    </div>
  );
}

export function InputTypeSelectForm({ atom }) {
  const errors = usePage().props?.errors || {};
  const factors = usePage().props?.factors?.data || [];

  const inputsAtom = useMemo(
    () => focusAtom(atom, (optic) => optic.prop("inputs")),
    [atom],
  );
  const inputsAtomsAtom = useMemo(() => splitAtom(inputsAtom), [atom]);

  const [basePayload, setBasePayload] = useAtom(atom);
  const [inputArrayAtoms, setInputsArray] = useAtom(inputsAtom);
  const inputsAtoms = useAtomValue(inputsAtomsAtom);

  const [emissionEnabled, setEmissionEnabled] = useState(
    inputArrayAtoms.some((i) => i.emission_factor_id),
  );

  const checkboxIds = useRef({
    emissionEnabled: uuid(),
    requireValue: uuid(),
    typeSingleSelect: uuid(),
    typeMultiSelect: uuid(),
  });

  const errorBag = basePayload.errorBag;

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setInputsArray((items) => {
        const oldIndex = items.findIndex(({ uuid }) => uuid === active.id);
        const newIndex = items.findIndex(({ uuid }) => uuid === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const handleAddInput = () => {
    setInputsArray((prev) => [...prev, { label: "", uuid: uuid() }]);
  };

  const handleRemoveInput = (uuid) => {
    setInputsArray((prev) => prev.filter((i) => i.uuid !== uuid));
  };

  const handleMetaToggle = (e, key, fromTargetValue = false) => {
    setBasePayload((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        [key]: fromTargetValue ? e.target.value : !prev?.meta?.[key],
      },
    }));
  };

  const toggleEmissions = () => {
    setEmissionEnabled((prev) => !prev);
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const isMultiSelect = basePayload?.meta?.select_type === "multi";

  const isInputUnitsMismatch = (() => {
    // Map emission factor IDs to their respective units
    const emissionFactorUnits = inputArrayAtoms
      .filter((i) => i.emission_factor_id !== null)
      .map((i) => {
        const factor = factors.find((f) => f.id === i.emission_factor_id);
        return factor ? factor.unit : null;
      })
      .filter((unit) => unit !== null);

    // Check if there is a mismatch in units
    const uniqueUnits = new Set(emissionFactorUnits);

    return uniqueUnits.size > 1;
  })();

  return (
    <div className="flex w-full flex-col items-start gap-8">
      {!basePayload?.is_published && (
        <p>
          Select allows you to give the reporter a choice between different
          answers. You can if you want add amount and emission factor to connect
          emission to the alternatives.
        </p>
      )}

      <form className="flex flex-wrap gap-x-8 gap-y-2">
        <p className="w-full">How would you like to collect answers?</p>
        <div>
          <input
            name="meta.select_type"
            value="single"
            type="radio"
            className="mr-2 p-2"
            id={checkboxIds.current.typeSingleSelect}
            checked={basePayload?.meta?.select_type !== "multi"}
            disabled={basePayload?.is_published}
            onChange={(e) => handleMetaToggle(e, "select_type", true)}
          />

          <label htmlFor={checkboxIds.current.typeSingleSelect}>
            Single select
          </label>
        </div>
        <div>
          <input
            name="meta.select_type"
            value="multi"
            type="radio"
            className="mr-2 p-2"
            id={checkboxIds.current.typeMultiSelect}
            checked={basePayload?.meta?.select_type === "multi" || false}
            disabled={basePayload?.is_published}
            onChange={(e) => handleMetaToggle(e, "select_type", true)}
          />

          <label htmlFor={checkboxIds.current.typeMultiSelect}>
            Multi select
          </label>
        </div>
        <FormError error={errors?.[errorBag]?.["meta.select_type"]} />
      </form>

      <div className="w-full">
        <LabeledInput
          name="question_text"
          title="What's your question?*"
          placeholder="Describe what the alternatives are all about"
          className="mb-0"
          value={basePayload?.question_text || ""}
          onChange={(e) =>
            setBasePayload((prev) => ({
              ...prev,
              [e.target.name]: e.target.value,
            }))
          }
          error={errors?.[errorBag]?.question_text}
        />
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-4">
        <div>
          <input
            type="checkbox"
            className="mr-2 p-2"
            id={checkboxIds.current.requireValue}
            checked={basePayload?.meta?.require_value || false}
            disabled={basePayload?.is_published}
            onChange={(e) => handleMetaToggle(e, "require_value")}
          />

          <label htmlFor={checkboxIds.current.requireValue}>
            Collect amount from reporter
          </label>
        </div>

        <FormError error={errors?.[errorBag]?.["meta.require_value"]} />

        <div>
          <input
            type="checkbox"
            className="mr-2 p-2"
            id={checkboxIds.current.emissionEnabled}
            checked={emissionEnabled}
            disabled={basePayload?.is_published}
            onChange={toggleEmissions}
          />

          <label htmlFor={checkboxIds.current.emissionEnabled}>
            Add emission factor
          </label>
        </div>
      </div>

      <div className="w-full">
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={inputArrayAtoms.map((item) => item.uuid)}
            strategy={verticalListSortingStrategy}
          >
            {inputsAtoms.map((item, i) => (
              <SortableItem
                key={inputArrayAtoms[i].uuid}
                item={item}
                i={i}
                emissionEnabled={emissionEnabled}
                collectFromUserEnabled={
                  basePayload?.meta?.require_value || false
                }
                isMultiSelect={isMultiSelect}
                errorBag={errorBag}
                onRemove={() => handleRemoveInput(inputArrayAtoms[i].uuid)}
              />
            ))}
          </SortableContext>
        </DndContext>

        <button
          className="text-blue underline hover:no-underline"
          onClick={handleAddInput}
        >
          <Plus className="mb-1 inline" size={18} /> Add more alternatives
        </button>
      </div>

      {emissionEnabled && isInputUnitsMismatch && (
        <p className="flex w-full max-w-none items-center gap-2 bg-[#fee1d6] p-4">
          <AlertCircle className="inline" stroke="#F96733" size={24} /> Please
          consider using emissions factors with the same reporting unit to avoid
          confusion in the results
        </p>
      )}

      {emissionEnabled && <DefinitionOfScopes />}
    </div>
  );
}
